import { Layout } from 'react-grid-layout';
import { z } from 'zod';

import { LayoutTypes } from '@work4all/models';
import { EMailTemplateKind } from '@work4all/models/lib/Enums/EMailTemplateKind.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { DeepRequired } from '@work4all/utils/lib/deep-required/DeepRequired';

import { IMaskUserConfig, SearchHistoryResults } from './types';
import { UseSettingOptions } from './use-setting';

export const widthSizeZod = z.object({ width: z.number() });
export type WidthSize = DeepRequired<z.infer<typeof widthSizeZod>>;

export const heightSizeZod = z.object({ height: z.number() });
export type HeightSize = DeepRequired<z.infer<typeof heightSizeZod>>;

// Configure shared settings here.
const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

const DEFAULT_LAYOUT_DEFINITIONS = [
  {
    id: null,
    name: 'Standard',
  },
];

const DEFAULT_ACTIVE_LAYOUT_ID = {
  [LayoutTypes.Customer]: null,
  [LayoutTypes.Supplier]: null,
  [LayoutTypes.Project]: null,
};

const DEFAULT_MASK_STATE: IMaskUserConfig = {};

/** Mask state. */
export function mask(options: {
  entityType: string;
}): UseSettingOptions<IMaskUserConfig> {
  const { entityType } = options;

  return {
    name: `mask.${entityType}`,
    scope: 'user',
    defaultValue: DEFAULT_MASK_STATE,
  };
}

const SEARCH_HISTORY_DEFAULT_VALUE: SearchHistoryResults = {
  [Entities.customer]: [],
  [Entities.supplier]: [],
  [Entities.project]: [],
  [Entities.article]: [],
};

/**
 * Search results for the global search component and file pages. The search
 * items are grouped by entity type.
 * */
export function searchHistory(): UseSettingOptions<SearchHistoryResults> {
  return {
    name: `searchHistory`,
    scope: 'tenant',
    defaultValue: SEARCH_HISTORY_DEFAULT_VALUE,
  };
}

type EntityPickerSettingsSort = {
  options: readonly string[];
};

export type EntityPickerSettingsFilterBy = {
  key: string;
  query: {
    $eq: number | boolean;
  };
};

type EntityPickerSettingsFilter = {
  options: EntityPickerSettingsFilterBy[];
};

export type EntityPickerColumnsVisibilityItem = {
  key?: string;
  isVisible?: boolean;
  width?: string;
};

type EntityPickerColumnsVisibility = {
  options: EntityPickerColumnsVisibilityItem[];
};

export type EntityPickerSettings = {
  sort: EntityPickerSettingsSort;
  filter: EntityPickerSettingsFilter;
  columns?: EntityPickerColumnsVisibility;
};

/**
 * ListEntityPicker filters
 * */
const settingsFilterBy = z.array(
  z.object({
    key: z.string(),
    query: z.object({
      $eq: z.union([z.number(), z.boolean()]),
    }),
  })
);
export type FilterPickerEntities = DeepRequired<
  z.infer<typeof settingsFilterBy>
>;

export const filterPickerEntities = (options: {
  entityType: string;
}): UseSettingOptions<FilterPickerEntities> => ({
  name: `filterPickerEntities.${options?.entityType}`,
  scope: 'user',
  defaultValue: [],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parseFn: settingsFilterBy.parse as any,
});

/**
 * ListEntityPicker sorting options
 * */
const settingsSortSchema = z.string();
export type SortPickerEntities = z.infer<typeof settingsSortSchema>;

export const sortPickerEntities = (options: {
  entityType: string;
  defaultValue?: string;
}): UseSettingOptions<SortPickerEntities> => ({
  name: `sortPickerEntities.${options.entityType}`,
  scope: 'user',
  defaultValue: options.defaultValue,
  parseFn: settingsSortSchema.parse,
});

/**
 * ListEntityPicker visible colmuns
 * */
const settingsVisibleERPickerColumns = z.array(
  z.object({
    key: z.string(),
    width: z.string(),
    isVisible: z.boolean(),
  })
);
export type SettingsVisibleERPickerColumns = z.infer<
  typeof settingsVisibleERPickerColumns
>;

const getGetERPPickerColumns = (
  dateFields: string
): EntityPickerColumnsVisibilityItem[] => [
  {
    key: dateFields,
    width: '1fr',
    isVisible: false,
  },
  {
    key: 'businessPartnerNumber',
    width: '1fr',
    isVisible: false,
  },
  {
    key: 'businessPartnerName',
    width: '2fr',
    isVisible: false,
  },
  {
    key: 'projectNumber',
    width: '1fr',
    isVisible: false,
  },
  {
    key: 'projectName',
    width: '2fr',
    isVisible: false,
  },
];

export const getGetPickerColumns = (
  entityType: string
): EntityPickerColumnsVisibilityItem[] => {
  switch (entityType) {
    case Entities.contract:
      return getGetERPPickerColumns('contractDate');
    case Entities.deliveryNote:
      return getGetERPPickerColumns('date');
    case Entities.inboundDeliveryNote:
      return getGetERPPickerColumns('date');
    case Entities.order:
      return getGetERPPickerColumns('date');
    // Add more entities when their pickers have to have additional columns in the list
    default:
      return [];
  }
};

const VISIBLE_PICKER_COLUMNS_DEFAULTS: Record<
  string,
  EntityPickerColumnsVisibilityItem[]
> = {
  [Entities.contract]: getGetERPPickerColumns('contractDate'),
  [Entities.deliveryNote]: getGetERPPickerColumns('date'),
  [Entities.inboundDeliveryNote]: getGetERPPickerColumns('date'),
  [Entities.order]: getGetERPPickerColumns('date'),
};

export const visiblePickerColumns = (options: {
  entityType: string;
}): UseSettingOptions<SettingsVisibleERPickerColumns> => ({
  name: `visiblePickerColumns.${options.entityType}`,
  scope: 'user',
  defaultValue: VISIBLE_PICKER_COLUMNS_DEFAULTS[options.entityType] ?? [],
  parseFn: settingsVisibleERPickerColumns.parse,
});

const isTicketSolutionRequieredZOD = z.boolean();
export function isTicketSolutionRequiered(): UseSettingOptions<
  DeepRequired<z.infer<typeof isTicketSolutionRequieredZOD>>
> {
  return {
    name: `Pflichtfeld Ticketlösung`,
    scope: 'global',
    defaultValue: false,
    parseFn: isTicketSolutionRequieredZOD.parse,
  };
}

export function projectPlanningTargetSource(): UseSettingOptions<
  Entities.contract | Entities.calculation
> {
  return {
    name: `projectPlanning.targetSource`,
    scope: 'user',
    defaultValue: Entities.contract,
  };
}

type LayoutResponsiveBreakpoint = 'lg' | 'md' | 'sm' | 'xs' | 'xxs';
type LayoutResponsiveDefinition = Record<LayoutResponsiveBreakpoint, Layout[]>;

const DEFAULT_FILE_WIDGET_LAYOUTS = {
  layouts: [],
};

/**
 * File Widgets layout per breakpoints
 * */
export const fileWidgetLayouts = (
  layoutId?: string
): UseSettingOptions<{
  layouts: LayoutResponsiveDefinition[];
}> => {
  return {
    name: `files.widgetLayouts_V2${layoutId ? '.' + layoutId : ''}`,
    scope: 'tenant',
    defaultValue: DEFAULT_FILE_WIDGET_LAYOUTS,
  };
};

const pageZoomLevelZod = z.number();
export type PageZoomLevel = DeepRequired<z.infer<typeof pageZoomLevelZod>>;
export function pageZoomLevel(): UseSettingOptions<PageZoomLevel> {
  return {
    name: 'Web.App.Page_Zoom',
    scope: 'user',
    defaultValue: 100,
    parseFn: pageZoomLevelZod.parse,
  };
}

const filePageWidgetGroupRegisterZod = z.record(z.string(), z.string());
export function filePageWidgetGroupRegister(
  layoutId?: string
): UseSettingOptions<z.infer<typeof filePageWidgetGroupRegisterZod>> {
  return {
    name: `filePageWidgetGroupRegister${layoutId ? '.' + layoutId : ''}`,
    scope: 'tenant',
    defaultValue: EMPTY_OBJECT,
    parseFn: filePageWidgetGroupRegisterZod.parse,
  };
}

const filePageDeletedWidgetGroupsZod = z.array(z.string());
export function filePageDeletedWidgetGroups(
  layoutId?: string
): UseSettingOptions<z.infer<typeof filePageDeletedWidgetGroupsZod>> {
  return {
    name: `filePageDeletedWidgetGroups${layoutId ? '.' + layoutId : ''}`,
    scope: 'tenant',
    defaultValue: EMPTY_ARRAY,
    parseFn: filePageDeletedWidgetGroupsZod.parse,
  };
}

const filePageAdditionalWidgetGroupsZod = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    layoutType: z.nativeEnum(LayoutTypes),
  })
);
export function filePageAdditionalWidgetGroups(
  layoutId?: string
): UseSettingOptions<z.infer<typeof filePageAdditionalWidgetGroupsZod>> {
  return {
    name: `filePageAdditionalWidgetGroups.${layoutId ? '.' + layoutId : ''}`,
    scope: 'tenant',
    defaultValue: EMPTY_ARRAY,
    parseFn: filePageAdditionalWidgetGroupsZod.parse,
  };
}

const filePageWidgetGroupNameRegisterZod = z.record(z.string(), z.string());
export function filePageWidgetGroupNameRegister(
  layoutId?: string
): UseSettingOptions<z.infer<typeof filePageWidgetGroupNameRegisterZod>> {
  return {
    name: `filePageWidgetGroupNameRegister${layoutId ? '.' + layoutId : ''}`,
    scope: 'tenant',
    defaultValue: EMPTY_OBJECT,
    parseFn: filePageWidgetGroupNameRegisterZod.parse,
  };
}

const filePageWidgetLayoutDefinitionsZod = z.array(
  z.object({
    id: z.string().nullable(),
    name: z.string(),
  })
);
export function filePageWidgetLayoutDefinitions(
  layoutType?: LayoutTypes
): UseSettingOptions<z.infer<typeof filePageWidgetLayoutDefinitionsZod>> {
  return {
    name: `filePageWidgetLayoutDefinitions${
      layoutType ? '.' + layoutType : ''
    }`,
    scope: 'tenant',
    defaultValue: DEFAULT_LAYOUT_DEFINITIONS,
    parseFn: filePageWidgetLayoutDefinitionsZod.parse,
  };
}

const activeFilePageWidgetLayoutDefinitionIdZod = z.object({
  [LayoutTypes.Customer]: z.string().nullable(),
  [LayoutTypes.Supplier]: z.string().nullable(),
  [LayoutTypes.Project]: z.string().nullable(),
});
export function activeFilePageWidgetLayoutDefinitionId(): UseSettingOptions<
  z.infer<typeof activeFilePageWidgetLayoutDefinitionIdZod>
> {
  return {
    name: `activeFilePageWidgetLayoutDefinitionId`,
    scope: 'tenant',
    defaultValue: DEFAULT_ACTIVE_LAYOUT_ID,
    parseFn: activeFilePageWidgetLayoutDefinitionIdZod.parse,
  };
}

export const widthSizeOptionalZod = z.object({ width: z.number().optional() });
type WidthSizeOptional = z.infer<typeof widthSizeZod>;
const DEFAULT_FILE_PREVIEW_SIZE: WidthSizeOptional = { width: undefined };
/** Preview size on list pages. */
export function pickerSize(options: {
  fileType: string;
}): UseSettingOptions<WidthSizeOptional> {
  const { fileType } = options;

  return {
    name: `file.${fileType}.pickerSize`,
    scope: 'user',
    defaultValue: DEFAULT_FILE_PREVIEW_SIZE,
    parseFn: widthSizeOptionalZod.parse,
  };
}

export const shouldPrefillTimeTrackingCommentFromTicketProblemDescriptionZod =
  z.boolean();
export function shouldPrefillTimeTrackingCommentFromTicketProblemDescription(): UseSettingOptions<boolean> {
  return {
    name: 'Ticket Standard Bemerkung',
    scope: 'global',
    defaultValue: false,
    parseFn:
      shouldPrefillTimeTrackingCommentFromTicketProblemDescriptionZod.parse,
  };
}

type StandardItems<K extends string = string> = {
  [T in K]?: string;
};

const DEFAULT_STANDARD_EMAIL_KINDS: StandardItems<EMailTemplateKind> =
  Object.keys(EMailTemplateKind).reduce((obj, key) => {
    obj[EMailTemplateKind[key]] = null;
    return obj;
  }, {});

export function standardEmailTemplates(): UseSettingOptions<
  StandardItems<EMailTemplateKind>
> {
  return {
    name: `StandardEMailTemplates`,
    scope: 'user',
    defaultValue: DEFAULT_STANDARD_EMAIL_KINDS,
  };
}
